import React, { useCallback, useState, useEffect } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  BackgroundVariant,
  Panel,
  Handle,
  Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import {
  Zap,
  Database,
  Brain,
  Users,
  GitBranch,
  Send,
  Bell,
  Shuffle,
  Eye,
  Sparkles,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// Dagre layout function - automatically positions nodes perfectly
const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 150, ranksep: 200 }); // LR = Left to Right, large spacing

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 280, height: 180 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 140, // Center the node
        y: nodeWithPosition.y - 90,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Node type definitions with premium styling
const nodeTypes = {
  TRIGGER: { icon: Zap, color: 'from-green-400 to-emerald-600', iconColor: 'text-white' },
  INTEGRATION: { icon: Database, color: 'from-blue-400 to-blue-600', iconColor: 'text-white' },
  AI_PROCESSING: { icon: Brain, color: 'from-purple-400 to-purple-600', iconColor: 'text-white' },
  DATA_TRANSFORM: { icon: Shuffle, color: 'from-cyan-400 to-cyan-600', iconColor: 'text-white' },
  DECISION: { icon: GitBranch, color: 'from-yellow-400 to-amber-600', iconColor: 'text-white' },
  HUMAN_REVIEW: { icon: Users, color: 'from-orange-400 to-orange-600', iconColor: 'text-white' },
  ACTION: { icon: Send, color: 'from-indigo-400 to-indigo-600', iconColor: 'text-white' },
  NOTIFICATION: { icon: Bell, color: 'from-pink-400 to-pink-600', iconColor: 'text-white' }
};

interface AIWorkflowNode {
  id: string;
  type: string;
  label: string;
  description: string;
  technology?: string;
  config?: any;
  ai_specifics?: any;
  position: { x: number; y: number };
  data?: any;
}

interface AIWorkflowData {
  workflow_name: string;
  description: string;
  complexity?: string;
  estimated_setup_time?: string;
  nodes: AIWorkflowNode[];
  edges: Edge[];
  estimated_benefits?: any;
}

interface AIWorkflowDiagramProps {
  workflowData: AIWorkflowData;
  onNodeClick?: (node: Node) => void;
}

// Custom Node Component with premium animations
const CustomNode = ({ data, selected }: { data: any; selected: boolean }) => {
  const nodeConfig = nodeTypes[data.type as keyof typeof nodeTypes] || nodeTypes.ACTION;
  const Icon = nodeConfig.icon;

  return (
    <div
      className={`relative ${selected ? 'ring-4 ring-blue-400 ring-opacity-50' : ''}`}
      style={{ minWidth: '220px', maxWidth: '280px' }}
    >
      {/* Input Handle - connection point for incoming edges */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#3b82f6', width: '12px', height: '12px' }}
      />

      {/* Glow effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>

      {/* Main node container */}
      <div className={`relative bg-gradient-to-br ${nodeConfig.color} rounded-xl shadow-2xl p-4 transition-all duration-300 hover:scale-105 hover:shadow-3xl`}>
        {/* Icon badge */}
        <div className="absolute -top-3 -right-3 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center animate-pulse">
          <Icon className={`w-6 h-6 ${nodeConfig.iconColor.replace('white', 'gray-700')}`} />
        </div>

        {/* Node content */}
        <div className="text-white">
          <div className="text-xs font-semibold uppercase tracking-wider opacity-90 mb-1">
            {data.type.replace(/_/g, ' ')}
          </div>
          <div className="text-base font-bold mb-2">{data.label}</div>

          {data.description && (
            <div className="text-xs opacity-90 mb-2 line-clamp-2">
              {data.description}
            </div>
          )}

          {data.technology && (
            <div className="mt-2 pt-2 border-t border-white border-opacity-30">
              <div className="text-[10px] font-semibold uppercase opacity-75">Technology</div>
              <div className="text-xs font-medium">{data.technology}</div>
            </div>
          )}

          {/* AI-specific indicators */}
          {data.ai_specifics && (
            <div className="mt-2 pt-2 border-t border-white border-opacity-30">
              <div className="flex items-center gap-2 text-xs">
                <Sparkles className="w-3 h-3" />
                <span>AI Confidence: {data.ai_specifics.confidence_threshold}%</span>
              </div>
            </div>
          )}
        </div>

        {/* Animated corner accent */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-white opacity-10 rounded-bl-full animate-pulse"></div>
      </div>

      {/* Output Handle - connection point for outgoing edges */}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#3b82f6', width: '12px', height: '12px' }}
      />
    </div>
  );
};

const AIWorkflowDiagram: React.FC<AIWorkflowDiagramProps> = ({ workflowData, onNodeClick }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNode, setSelectedNode] = useState<any | null>(null);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    if (workflowData) {
      // Transform nodes with custom styling
      const transformedNodes: Node[] = workflowData.nodes.map((node) => ({
        id: node.id,
        type: 'custom',
        position: { x: 0, y: 0 }, // Initial position, will be overridden by dagre
        data: {
          ...node,
          type: node.type
        },
        draggable: true
      }));

      // Transform edges with animations
      const transformedEdges: Edge[] = workflowData.edges.map((edge, idx) => ({
        ...edge,
        id: edge.id || `edge-${idx}`,
        type: 'smoothstep',
        animated: isAnimating,
        style: {
          stroke: '#3b82f6',
          strokeWidth: 3
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#3b82f6',
          width: 25,
          height: 25
        },
        label: edge.label,
        labelStyle: {
          fill: '#3b82f6',
          fontWeight: 600,
          fontSize: 12
        },
        labelBgStyle: {
          fill: '#ffffff',
          fillOpacity: 0.9
        }
      }));

      // Use dagre to automatically layout nodes with perfect spacing
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        transformedNodes,
        transformedEdges
      );

      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    }
  }, [workflowData, isAnimating]);

  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node.data);
    if (onNodeClick) {
      onNodeClick(node);
    }
  }, [onNodeClick]);

  const toggleAnimation = () => {
    setIsAnimating(!isAnimating);
  };

  if (!workflowData) {
    return (
      <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Generating AI workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col" style={{ height: '700px' }}>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-lg">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-6 h-6" />
              <h3 className="text-2xl font-bold">{workflowData.workflow_name}</h3>
            </div>
            <p className="text-sm opacity-90 mb-3">{workflowData.description}</p>

            <div className="flex flex-wrap gap-4 text-sm">
              {workflowData.complexity && (
                <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  <AlertCircle className="w-4 h-4" />
                  <span>Complexity: {workflowData.complexity}</span>
                </div>
              )}
              {workflowData.estimated_setup_time && (
                <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  <CheckCircle className="w-4 h-4" />
                  <span>Setup Time: {workflowData.estimated_setup_time}</span>
                </div>
              )}
              <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                <Database className="w-4 h-4" />
                <span>{workflowData.nodes.length} Steps</span>
              </div>
            </div>
          </div>

          <button
            onClick={toggleAnimation}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-all flex items-center gap-2"
          >
            {isAnimating ? <Eye className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
            <span className="text-sm">{isAnimating ? 'Pause' : 'Animate'}</span>
          </button>
        </div>
      </div>

      {/* React Flow Diagram */}
      <div className="flex-1 bg-gray-50 rounded-b-lg overflow-hidden relative" style={{ minHeight: '600px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={{
            custom: CustomNode
          }}
          fitView
          minZoom={0.5}
          maxZoom={1.5}
          defaultEdgeOptions={{
            animated: isAnimating
          }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={16}
            size={1}
            color="#e5e7eb"
          />
          <Controls
            className="bg-white shadow-lg rounded-lg border border-gray-200"
          />

          {/* Legend Panel */}
          <Panel position="bottom-right" className="bg-white shadow-2xl rounded-lg p-4 border border-gray-200">
            <div className="text-xs font-bold text-gray-700 mb-3">Node Types</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(nodeTypes).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <div key={type} className="flex items-center gap-2">
                    <div className={`w-6 h-6 bg-gradient-to-br ${config.color} rounded flex items-center justify-center`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[10px] text-gray-600">{type.replace(/_/g, ' ')}</span>
                  </div>
                );
              })}
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Selected Node Details Sidebar */}
      {selectedNode && (
        <div className="absolute top-24 right-4 w-80 bg-white shadow-2xl rounded-xl border border-gray-200 p-5 animate-slide-in-right max-h-[70vh] overflow-y-auto">
          <button
            onClick={() => setSelectedNode(null)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>

          <div className="mb-4">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
              {selectedNode.type?.replace(/_/g, ' ')}
            </div>
            <h4 className="text-lg font-bold text-gray-900">{selectedNode.label}</h4>
          </div>

          {selectedNode.description && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-700 mb-1">Description</div>
              <p className="text-sm text-gray-600">{selectedNode.description}</p>
            </div>
          )}

          {selectedNode.technology && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-700 mb-1">Technology</div>
              <p className="text-sm text-gray-900 font-medium">{selectedNode.technology}</p>
            </div>
          )}

          {selectedNode.config && Object.keys(selectedNode.config).length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-700 mb-2">Configuration</div>
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                {Object.entries(selectedNode.config).map(([key, value]) => (
                  <div key={key} className="text-xs">
                    <span className="font-semibold text-gray-600">{key.replace(/_/g, ' ')}:</span>
                    <span className="ml-2 text-gray-800">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedNode.ai_specifics && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Specifications
              </div>
              <div className="space-y-2 text-xs">
                {selectedNode.ai_specifics.confidence_threshold && (
                  <div>
                    <span className="font-semibold text-purple-700">Confidence Threshold:</span>
                    <span className="ml-2 text-purple-900">{selectedNode.ai_specifics.confidence_threshold}%</span>
                  </div>
                )}
                {selectedNode.ai_specifics.auto_approve_threshold && (
                  <div>
                    <span className="font-semibold text-purple-700">Auto-Approve:</span>
                    <span className="ml-2 text-purple-900">≥{selectedNode.ai_specifics.auto_approve_threshold}%</span>
                  </div>
                )}
                {selectedNode.ai_specifics.human_review_threshold && (
                  <div>
                    <span className="font-semibold text-purple-700">Human Review:</span>
                    <span className="ml-2 text-purple-900">&lt;{selectedNode.ai_specifics.human_review_threshold}%</span>
                  </div>
                )}
                {selectedNode.ai_specifics.fallback && (
                  <div className="pt-2 border-t border-purple-200">
                    <span className="font-semibold text-purple-700">Fallback:</span>
                    <p className="ml-0 mt-1 text-purple-900">{selectedNode.ai_specifics.fallback}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIWorkflowDiagram;
